/**
 * @file CompanyMeetingDetailPage.tsx
 * @brief Company Admin → single MeetingIntel meeting (data wrapper).
 *
 * @details
 * Thin wrapper: fetches one company-scoped boardroom meeting and delegates
 * rendering to the shared `MeetingDetailView` (header / score strip / tabs /
 * rail). Handles the loading + error states; the immersive body is scope-
 * agnostic and reused by the super-admin page.
 *
 * @dependencies
 * - react-router-dom (useParams, useNavigate)
 * - components/ui/Skeleton, components/feedback/ErrorState
 * - features/company-meetings/{api, components/MeetingDetailView}
 *
 * @references
 * - features/company-meetings/components/MeetingDetailView.tsx
 * - features/admin-meetings/AdminMeetingDetailPage.tsx (sibling wrapper)
 *
 * @author DES
 * @date 08-07-2026
 */

import { useLocation, useNavigate, useParams } from "react-router-dom";
import { readBackState } from "@/shared/navigation/backState";
import { Skeleton } from "@/components/ui/Skeleton";
import { ErrorState } from "@/components/feedback/ErrorState";
import { HistoryLockedState } from "@/shared/history/HistoryLockedState";
import { isHistoryLockedError } from "@/shared/history/historyLock";
import {
  GridBand,
  GridBlock,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { useCompanyMeetingDetail } from "./api";
import { isNotesTierMeeting } from "./schemas";
import { hasNotesPreviewFlag } from "./notesDoc";
import { MeetingDetailView } from "./components/MeetingDetailView";
import { EmployeeSpeakerResolvePanel } from "@/features/employee-boardroom/EmployeeSpeakerResolvePanel";
import { MeetingFeedbackPrompt } from "@/features/employee-boardroom/MeetingFeedbackPrompt";

export function CompanyMeetingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const detail = useCompanyMeetingDetail(id ?? null);
  const meeting = detail.data;

  // A Meeting Notes recording lives in the Notes view, not the Meetings list
  // (ADR-030, 06-10-2026), so its back link goes there.
  // The dev Notes v2 preview (notesDoc.ts) was opened from Notes too
  // (DES 09-10-2026).
  const location = useLocation();
  const isNotes = isNotesTierMeeting(meeting) || hasNotesPreviewFlag(location.search);
  // Back to where the meeting was opened from (a to-do, Home, Inbox), else
  // the list (DES 13-10-2026).
  const back = readBackState(location.state);
  const backToList = () => navigate(back?.backTo ?? (isNotes ? "/company/notes" : "/company/meetings"));

  if (detail.isLoading) {
    return (
      <div className={gridPageBleedClass("pb-6")}>
        <GridBlock>
          <GridBand className="space-y-3">
            <Skeleton className="h-8 w-40" />
            <Skeleton className="h-10 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
          </GridBand>
          <GridBand>
            <Skeleton className="h-28 w-full" />
          </GridBand>
          <GridBand>
            <Skeleton className="h-64 w-full" />
          </GridBand>
        </GridBlock>
      </div>
    );
  }

  // B39: older than this user's history window (403 HISTORY_LOCKED).
  if (isHistoryLockedError(detail.error)) {
    return (
      <div className={gridPageBleedClass("pb-6")}>
        <GridBlock>
          <GridBand>
            <HistoryLockedState error={detail.error} onBack={backToList} />
          </GridBand>
        </GridBlock>
      </div>
    );
  }

  if (detail.error || !meeting) {
    return (
      <div className={gridPageBleedClass("pb-6")}>
        <GridBlock>
          <GridBand>
            <ErrorState
              title="Couldn't load this meeting"
              error={
                detail.error ??
                "It may have been deleted or you no longer have access."
              }
              onRetry={() => detail.refetch()}
            />
          </GridBand>
        </GridBlock>
      </div>
    );
  }

  const isReady = meeting.status === "ready";

  return (
    <MeetingDetailView
      meeting={meeting}
      scope="company"
      layout="phone"
      onBack={backToList}
      backLabel={back?.backLabel ?? (isNotes ? "Notes" : "Meeting Intel")}
      headerExtra={
        isReady ? (
          <MeetingFeedbackPrompt meetingId={meeting.id} meetingReady />
        ) : null
      }
      resolvePanel={
        meeting.status === "resolved_pending" ? (
          <EmployeeSpeakerResolvePanel key={meeting.id} meeting={meeting} />
        ) : undefined
      }
    />
  );
}
